import type { TFunction } from 'i18next';
import type { FrequencyOptionsResponse, FrequencyUnit } from '@api/customer-portal-api';

// Helpers for the change-frequency offer page. The walk rules live in ./retentionFlow.

// "Every 2 months", "Every week", … Cards are labeled from the options response's
// normalized value and unit (the backend picks the largest exact unit).
export const formatFrequency = (t: TFunction, value: number, unit: FrequencyUnit): string =>
  t(`retention.frequency.every_${unit}`, { count: value });

// "2 months", "week", … the interval after a price ("$20 / 2 months").
export const formatFrequencyInterval = (t: TFunction, value: number, unit: FrequencyUnit): string =>
  t(`retention.frequency.interval_${unit}`, { count: value });

// The change may have been applied (gateway error, or the request never got an
// answer). The portal must not treat it as failed, and must never cancel on it.
const isUnconfirmed = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return status === undefined || status === 502 || status === 503 || status === 504;
};

// Accept button label: "Try again" only after this button's own request failed,
// never after a failed cancel on the same page.
export const frequencyAcceptLabel = (
  t: TFunction,
  state: { busy: boolean; err: string | null; acceptFailed: boolean; acceptLabel?: string | null },
): string =>
  state.busy
    ? t('common.pleaseWait')
    : state.acceptFailed && state.err
      ? t('retention.frequency.tryAgain')
      : state.acceptLabel || t('retention.frequency.accept');

// How a failed accept is shown. The backend's errors carry no codes, so this branches on
// the HTTP status: "pending" = the change may have been applied (503, 502, 504 or no
// answer): the "confirming" view, never a cancel; "unavailable" = 409: the schedules and
// accept are hidden; "failed" = a definite failure (400, 500, …): an inline error and
// "Try again".
export type FrequencyAcceptFailure = 'pending' | 'unavailable' | 'failed';

export const classifyFrequencyAcceptError = (err: unknown): FrequencyAcceptFailure => {
  if (isUnconfirmed(err)) return 'pending';
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return status === 409 ? 'unavailable' : 'failed';
};

export type FrequencyOptionRow = FrequencyOptionsResponse['options'][number];

// The schedule the customer picked, or the first (shortest) one by default.
export const selectedFrequencyOption = (
  data: FrequencyOptionsResponse | null | undefined,
  optionId: string | null,
): FrequencyOptionRow | null =>
  data?.options.find((option) => option.option_id === optionId) ?? data?.options[0] ?? null;

// The next order date after a confirmed switch: the one Whop set, from the accept's
// response. The option's date is only a prediction, so it is the fallback.
export const confirmedNextOrderDate = (option: FrequencyOptionRow, response: unknown): string => {
  const date = (response as { next_payment_date?: unknown } | null)?.next_payment_date;
  return typeof date === 'string' && date ? date : option.next_payment_date;
};

// Same rule as the backend's days_to_frequency: the largest unit that divides the day
// count exactly (years = 365, months = 30, weeks = 7), else days.
const UNIT_DAYS: ReadonlyArray<[FrequencyUnit, number]> = [
  ['years', 365],
  ['months', 30],
  ['weeks', 7],
];

export const frequencyFromDays = (
  days: number | null | undefined,
): { value: number; unit: FrequencyUnit } | null => {
  if (typeof days !== 'number' || !Number.isInteger(days) || days < 1) return null;
  for (const [unit, size] of UNIT_DAYS) {
    if (days % size === 0) return { value: days / size, unit };
  }
  return { value: days, unit: 'days' };
};

const labelFromDays =
  (format: typeof formatFrequency) =>
  (
    t: TFunction,
    frequency: string,
    billingPeriodDays: number | null | undefined,
    isKnownKey: (frequency: string) => boolean,
  ): string | null => {
    if (isKnownKey(frequency)) return null;
    const cadence = frequencyFromDays(billingPeriodDays);
    return cadence ? format(t, cadence.value, cadence.unit) : null;
  };

// Dashboard label for a cadence outside the portal's known frequency keys, such as one a
// cancellation offer set ("every 45 days", "triweekly", "semiannual"), read from
// billing_period_days so it translates. null = keep the existing label.
export const cadenceLabelFromDays = labelFromDays(formatFrequency);

// The same, as the interval after a price ("45 days", "3 weeks").
export const cadenceIntervalFromDays = labelFromDays(formatFrequencyInterval);

// Arrow keys in the schedule options (the WAI-ARIA radio group pattern): Down/Right go to
// the next option and Up/Left to the previous one, wrapping; Home/End go to the first/last.
// null = not a movement key.
export const radioIndexForKey = (index: number, key: string, count: number): number | null => {
  if (count < 1) return null;
  switch (key) {
    case 'ArrowDown':
    case 'ArrowRight':
      return (index + 1) % count;
    case 'ArrowUp':
    case 'ArrowLeft':
      return (index - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
};
